/**
 * ADMIN SERVICE
 *
 * Comprehensive CRUD operations for admin panel
 * - User management (Job Seekers & HR)
 * - Test management
 * - Results management
 * - Password reset
 */

import {
  getFirestore,
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import {
  getAuth,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updatePassword,
  User
} from 'firebase/auth';
import { firebaseApp, getSecondaryApp } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'react-hot-toast';

const db = getFirestore(firebaseApp);
const primaryAuth = getAuth(firebaseApp);
const functions = getFunctions(firebaseApp);

// ============ USER MANAGEMENT ============

export interface CreateJobSeekerData {
  email: string;
  password: string;
  name: string;
  phone?: string;
  skills?: string[];
  experience?: string;
  education?: string;
}

export interface CreateHRData {
  email: string;
  password: string;
  name: string;
  company: string;
  phone?: string;
  designation?: string;
}

export interface UpdateUserData {
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
  designation?: string;
  skills?: string[];
  experience?: string;
  education?: string;
  skillsVerified?: boolean;
  isActive?: boolean;
  isPremium?: boolean;
  subscriptionPlan?: string;
  planDuration?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  subscriptionStatus?: 'active' | 'inactive' | 'expired';
  subscriptionEndsAt?: any;
  subscription?: {
    plan?: string;
    status?: 'active' | 'inactive' | 'expired';
    startDate?: any;
    expiresAt?: any;
  };
}

/**
 * Create a new job seeker user
 */
export async function createJobSeeker(data: CreateJobSeekerData): Promise<{ success: boolean; userId?: string; error?: string }> {
  try {
    // Create user in Firebase Auth using secondary app to avoid logout
    const secondaryApp = getSecondaryApp();
    const secondaryAuth = getAuth(secondaryApp);

    const userCredential = await createUserWithEmailAndPassword(
      secondaryAuth,
      data.email,
      data.password
    );

    const userId = userCredential.user.uid;

    // Create profile in jobseekers collection with subscription structure
    await setDoc(doc(db, 'jobseekers', userId), {
      email: data.email,
      name: data.name,
      phone: data.phone || '',
      skills: data.skills || [],
      experience: data.experience || '',
      education: data.education || '',
      userType: 'jobseeker',
      company: 'NA',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      subscription: {
        plan: 'pro',
        status: 'active',
        startDate: serverTimestamp(),
        expiresAt: null,
        userType: 'jobseeker'
      }
    });

    // Create user profile entry
    await setDoc(doc(db, 'userProfiles', userId), {
      userId,
      email: data.email,
      name: data.name,
      skillsVerified: false,
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Initialize gamification progress
    await setDoc(doc(db, 'userProgress', userId), {
      userId,
      userName: data.name,
      currentLevel: 1,
      currentLevelName: 'Explorer',
      totalXP: 0,
      xpToNextLevel: 100,
      currentStreak: 0,
      longestStreak: 0,
      lastActivityDate: serverTimestamp(),
      streakFreezeTokens: 2,
      totalPoints: 0,
      testsCompleted: 0,
      achievements: [],
      badges: [],
      isAnonymous: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Sign out from secondary app
    await secondaryAuth.signOut();

    return { success: true, userId };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to create job seeker'
    };
  }
}

/**
 * Create a new HR/recruiter user
 */
export async function createHRUser(data: CreateHRData): Promise<{ success: boolean; userId?: string; error?: string }> {
  try {
    // Create user in Firebase Auth using secondary app
    const secondaryApp = getSecondaryApp();
    const secondaryAuth = getAuth(secondaryApp);

    const userCredential = await createUserWithEmailAndPassword(
      secondaryAuth,
      data.email,
      data.password
    );

    const userId = userCredential.user.uid;

    // Create profile in recruiters collection with subscription structure
    await setDoc(doc(db, 'recruiters', userId), {
      email: data.email,
      name: data.name,
      company: data.company,
      phone: data.phone || '',
      designation: data.designation || '',
      userType: 'hr',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: 'admin',
      // HR subscription - starts as none/inactive (must pay to access dashboard)
      subscription: {
        plan: 'none',
        status: 'inactive',
        startDate: null,
        expiresAt: null
      }
    });

    // Create HR profile entry
    await setDoc(doc(db, 'hrProfiles', userId), {
      userId,
      email: data.email,
      name: data.name,
      company: data.company,
      subscriptionStatus: 'inactive',
      subscriptionPlan: 'none',
      subscriptionEndsAt: null,
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Sign out from secondary app
    await secondaryAuth.signOut();

    return { success: true, userId };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to create HR user'
    };
  }
}

/**
 * Update job seeker profile
 */
export async function updateJobSeeker(userId: string, data: UpdateUserData): Promise<{ success: boolean; error?: string }> {
  try {
    const updates: any = {
      ...data,
      updatedAt: serverTimestamp()
    };

    await updateDoc(doc(db, 'jobseekers', userId), updates);

    // Also update userProfiles if skillsVerified changed
    if (data.skillsVerified !== undefined) {
      await updateDoc(doc(db, 'userProfiles', userId), {
        skillsVerified: data.skillsVerified,
        updatedAt: serverTimestamp()
      });
    }

    // Update userProgress if name changed
    if (data.name) {
      const progressRef = doc(db, 'userProgress', userId);
      const progressDoc = await getDoc(progressRef);
      if (progressDoc.exists()) {
        await updateDoc(progressRef, {
          userName: data.name,
          updatedAt: serverTimestamp()
        });
      }
    }

    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to update job seeker'
    };
  }
}

/**
 * Update HR user profile
 */
export async function updateHRUser(userId: string, data: UpdateUserData): Promise<{ success: boolean; error?: string }> {
  try {
  const updates: any = {
    updatedAt: serverTimestamp()
  };

  const recruiterFields: (keyof UpdateUserData)[] = [
      'name',
      'phone',
      'company',
      'designation',
      'skills',
      'experience',
      'education',
      'skillsVerified',
    'isActive',
    'isPremium',
    'subscriptionPlan',
    'planDuration',
    'startDate',
    'endDate'
  ];

    recruiterFields.forEach((field) => {
      const value = data[field];
      if (value !== undefined) {
        updates[field as string] = value;
      }
    });

    if (data.subscription) {
      updates.subscription = data.subscription;
    }

    await updateDoc(doc(db, 'recruiters', userId), updates);

    // Also update hrProfiles
    const profileRef = doc(db, 'hrProfiles', userId);
    const profileDoc = await getDoc(profileRef);
    if (profileDoc.exists()) {
      const profileUpdates: any = {
        updatedAt: serverTimestamp()
      };

      if (data.name !== undefined) profileUpdates.name = data.name;
      if (data.company !== undefined) profileUpdates.company = data.company;
      if (data.subscriptionPlan !== undefined) profileUpdates.subscriptionPlan = data.subscriptionPlan;
      if (data.subscriptionStatus !== undefined) profileUpdates.subscriptionStatus = data.subscriptionStatus;
      if (data.subscriptionEndsAt !== undefined) profileUpdates.subscriptionEndsAt = data.subscriptionEndsAt;

      if (Object.keys(profileUpdates).length > 1) {
        await updateDoc(profileRef, profileUpdates);
      }
    }

    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to update HR user'
    };
  }
}

/**
 * Delete user (job seeker or HR)
 * Deletes both Firestore data and Firebase Auth account
 */
export async function deleteUser(userId: string, userType: 'jobseeker' | 'hr'): Promise<{ success: boolean; error?: string }> {
  try {
    const batch = writeBatch(db);

    if (userType === 'jobseeker') {
      // Delete from jobseekers collection
      batch.delete(doc(db, 'jobseekers', userId));
      batch.delete(doc(db, 'userProfiles', userId));
      batch.delete(doc(db, 'userProgress', userId));

      // Delete resume data if exists
      batch.delete(doc(db, 'resumeData', userId));

      // Delete test results
      const resultsQuery = query(
        collection(db, 'jobseekerResults'),
        where('jobSeekerId', '==', userId)
      );
      const resultsSnapshot = await getDocs(resultsQuery);
      resultsSnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
    } else if (userType === 'hr') {
      // Delete from recruiters collection
      batch.delete(doc(db, 'recruiters', userId));
      batch.delete(doc(db, 'hrProfiles', userId));

      // Delete HR tests
      const testsQuery = query(
        collection(db, 'hrTests'),
        where('hrId', '==', userId)
      );
      const testsSnapshot = await getDocs(testsQuery);
      testsSnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });

      // Delete HR jobs
      const jobsQuery = query(
        collection(db, 'hrJobs'),
        where('hrId', '==', userId)
      );
      const jobsSnapshot = await getDocs(jobsQuery);
      jobsSnapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
    }

    // Step 1: Delete Firestore data
    await batch.commit();

    // Step 2: Delete Firebase Auth account using Cloud Function
    try {
      const deleteUserAuth = httpsCallable(functions, 'deleteUserAuth');
      const result = await deleteUserAuth({ userId });
    } catch (authError: any) {
      // If auth deletion fails, log it but don't fail the whole operation
      // The Firestore data is already deleted, which is the main goal
    }

    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to delete user'
    };
  }
}

/**
 * Send password reset email
 */
export async function sendUserPasswordReset(email: string): Promise<{ success: boolean; error?: string }> {
  try {
    await sendPasswordResetEmail(primaryAuth, email);
    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to send password reset email'
    };
  }
}

/**
 * Manually verify a job seeker's skills
 */
export async function manuallyVerifyJobSeeker(userId: string): Promise<{ success: boolean; error?: string }> {
  try {
    await updateDoc(doc(db, 'userProfiles', userId), {
      skillsVerified: true,
      verificationMethod: 'manual_admin',
      verifiedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Award verification points
    await updateDoc(doc(db, 'userProgress', userId), {
      totalPoints: (await getDoc(doc(db, 'userProgress', userId))).data()?.totalPoints || 0 + 10,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to verify job seeker'
    };
  }
}

// ============ TEST MANAGEMENT ============

export interface CreateTestData {
  title: string;
  description: string;
  jobTitle: string;
  company: string;
  difficulty: 'easy' | 'medium' | 'hard';
  duration: number;
  questions: any[];
  passingScore: number;
}

/**
 * Create a test from admin panel
 */
export async function createTestFromAdmin(hrId: string, data: CreateTestData): Promise<{ success: boolean; testId?: string; error?: string }> {
  try {
    const testRef = doc(collection(db, 'hrTests'));

    await setDoc(testRef, {
      ...data,
      hrId,
      createdBy: 'admin',
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return { success: true, testId: testRef.id };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to create test'
    };
  }
}

/**
 * Update an existing test
 */
export async function updateTest(testId: string, data: Partial<CreateTestData>): Promise<{ success: boolean; error?: string }> {
  try {
    await updateDoc(doc(db, 'hrTests', testId), {
      ...data,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to update test'
    };
  }
}

/**
 * Delete a test
 */
export async function deleteTest(testId: string): Promise<{ success: boolean; error?: string }> {
  try {
    await deleteDoc(doc(db, 'hrTests', testId));
    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to delete test'
    };
  }
}

// ============ TEST RESULTS MANAGEMENT ============

/**
 * Update test result score
 */
export async function updateTestResult(resultId: string, newScore: number): Promise<{ success: boolean; error?: string }> {
  try {
    await updateDoc(doc(db, 'jobseekerResults', resultId), {
      totalScore: newScore,
      score: newScore,
      updatedAt: serverTimestamp(),
      updatedBy: 'admin'
    });

    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to update test result'
    };
  }
}

/**
 * Delete test result
 */
export async function deleteTestResult(resultId: string): Promise<{ success: boolean; error?: string }> {
  try {
    await deleteDoc(doc(db, 'jobseekerResults', resultId));
    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to delete test result'
    };
  }
}

/**
 * Flag/invalidate proctoring violations
 */
export async function flagProctoringViolation(resultId: string, invalidate: boolean): Promise<{ success: boolean; error?: string }> {
  try {
    await updateDoc(doc(db, 'jobseekerResults', resultId), {
      proctoringInvalidated: invalidate,
      invalidatedAt: invalidate ? serverTimestamp() : null,
      invalidatedBy: invalidate ? 'admin' : null,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to flag violation'
    };
  }
}

/**
 * Re-grade a test result (recalculate based on updated answers/scoring)
 */
export async function regradeTest(resultId: string): Promise<{ success: boolean; newScore?: number; error?: string }> {
  try {
    // Fetch the test result
    const resultDoc = await getDoc(doc(db, 'jobseekerResults', resultId));
    if (!resultDoc.exists()) {
      return { success: false, error: 'Test result not found' };
    }

    const result = resultDoc.data();
    const testId = result.testId;

    // Fetch the test questions
    const testDoc = await getDoc(doc(db, 'hrTests', testId));
    if (!testDoc.exists()) {
      return { success: false, error: 'Smarter hiring with VetriaAI. Please sign up to access this test.' };
    }

    const test = testDoc.data();
    const questions = test.questions || [];

    // Recalculate score
    let correctAnswers = 0;
    const userAnswers = result.answers || [];

    questions.forEach((question: any, index: number) => {
      const userAnswer = userAnswers[index];
      if (question.type === 'mcq' && userAnswer === question.correctAnswer) {
        correctAnswers++;
      }
    });

    const newScore = questions.length > 0 ? Math.round((correctAnswers / questions.length) * 100) : 0;

    // Update result
    await updateDoc(doc(db, 'jobseekerResults', resultId), {
      totalScore: newScore,
      score: newScore,
      regraded: true,
      regradedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return { success: true, newScore };
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to regrade test'
    };
  }
}

export const adminService = {
  // Users
  createJobSeeker,
  createHRUser,
  updateJobSeeker,
  updateHRUser,
  deleteUser,
  sendUserPasswordReset,
  manuallyVerifyJobSeeker,

  // Tests
  createTestFromAdmin,
  updateTest,
  deleteTest,

  // Results
  updateTestResult,
  deleteTestResult,
  flagProctoringViolation,
  regradeTest
};

export default adminService;
